import { CustomRoute } from "@/interfaces";
import StripePayment from "@/modules/Employee/StripePayment";
import { useUserStore } from "@/stores/userStore";
import {
  AppWindowMac,
  Bell,
  Building2,
  CheckSquare,
  DollarSign,
  FileText,
  FolderKanban,
  LayoutDashboard,
  Receipt,
  Repeat,
  TrendingUp,
  User,
  Users,
} from "lucide-react";
import React from "react";

const Dashboard = React.lazy(() => import("../modules/Employee/Dashboard"));
const AssociatedClients = React.lazy(() => import("../modules/Employee/AssociatedClients/index"));
const Projects = React.lazy(() => import("../modules/Employee/Projects/index"));
const MyCommissions = React.lazy(() => import("../modules/Employee/MyCommissions"));
const Commission = React.lazy(() => import("../modules/Employee/Commission"));

const MyInvoices = React.lazy(() => import("../modules/Employee/Invoices/MyInvoices"));
const AllInvoices = React.lazy(() => import("../modules/Employee/Invoices/AllInvoices"));
const CreateInvoicePage = React.lazy(() => import("../modules/Employee/Invoices/CreateInvoicePage"));
// const VendorBills = React.lazy(() => import("../modules/Finance/VendorBillApprovals/AllBills"));
const VendorBills = React.lazy(() => import("../modules/Admin/ListVendorBills/AllBills"));
const VendorBillApprovals = React.lazy(() => import("../modules/Finance/VendorBillApprovals"));
const CreateBillPage = React.lazy(() => import("../modules/Employee/Bills/CreateBillPage"));
const RecurringInvoices = React.lazy(() => import("../modules/Admin/Invoices/RecurringInvoices"));

const EditProfile = React.lazy(() => import("../modules/Profile/EditProfile"));
const AllNotifications = React.lazy(() => import("../modules/Notifications/AllNotifications"));

const currentCompany = useUserStore.getState().currentCompany;
const isQuickBooks = currentCompany?.invoicingAccounting === "QuickBooks";

const employeeRoutes: CustomRoute[] = [
  {
    path: "/",
    id: "Employee",
    element: <Dashboard />,
    menuName: "Dashboard",
    icon: LayoutDashboard,
    highlightOn: ["dashboard"],
  },
  {
    path: "/associated-clients",
    id: "Employee",
    element: <AssociatedClients />,
    menuName: "Associated Clients",
    icon: Users,
    highlightOn: ["associated-clients"],
    permissionKey: "clientManagement",
  },
  {
    path: "/projects",
    id: "Employee",
    element: <Projects />,
    menuName: "My Projects",
    icon: FolderKanban,
    highlightOn: ["projects"],
    permissionKey: "projectManagement",
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Commissions",
    icon: DollarSign,
    highlightOn: ["my-commissions", "commission"],
    permissionKey: "commissionManagement",
    submenu: [
      {
        path: "/my-commissions",
        id: "Employee",
        element: <MyCommissions />,
        menuName: "My Commissions",
        icon: DollarSign,
        highlightOn: ["my-commissions"],
        permissionKey: "commissionManagement",
      },
      {
        path: "/commission",
        id: "Employee",
        element: <Commission />,
        menuName: "Track Commissions",
        icon: TrendingUp,
        highlightOn: ["commission"],
        permissionKey: "commissionManagement",
      },
    ],
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Financials",
    icon: AppWindowMac,
    highlightOn: ["invoices"],
    submenu: [
      {
        path: "/stripe-payment",
        id: "Employee",
        element: <StripePayment />,
        menuName: "Stripe Payment",
        icon: Building2,
        highlightOn: ["stripe-payment"],
      },
      {
        path: "/my-invoices",
        id: "Employee",
        element: <MyInvoices />,
        menuName: "My Invoices",
        icon: FileText,
        highlightOn: ["my-invoices"],
        permissionKey: "BillingInvoices",
      },
      {
        path: "/all-invoices",
        id: "Employee",
        element: <AllInvoices />,
        menuName: "All Invoices",
        icon: FileText,
        highlightOn: ["all-invoices"],
        permissionKey: "BillingInvoices",
      },
      {
        path: "/my-invoices/create",
        id: "Employee",
        element: <CreateInvoicePage />,
        menuName: "Create Invoice",
        icon: FileText,
        highlightOn: ["my-invoices"],
        permissionKey: "BillingInvoices",
      },
      {
        path: "/billing/recurring-invoices",
        id: "Employee",
        element: <RecurringInvoices />,
        menuName: "Recurring Transactions",
        icon: Repeat,
        highlightOn: ["recurring-invoices"],
        permissionKey: "BillingInvoices",
        hideInMenu: !isQuickBooks,
      },
      {
        path: "/vendor-bills",
        id: "Employee",
        element: <VendorBills />,
        menuName: "Vendor Bills",
        icon: Receipt,
        highlightOn: ["vendor-bills"],
        permissionKey: "BillingInvoices",
      },
      {
        path: "/vendor-bill-approvals",
        id: "Employee",
        element: <VendorBillApprovals />,
        menuName: "Vendor Bill Approvals",
        icon: CheckSquare,
        highlightOn: ["vendor-bill-approvals"],
      },
      {
        path: "/vendor-bills/create",
        id: "Employee",
        element: <CreateBillPage />,
        menuName: "Create Bill",
        icon: FileText,
        highlightOn: ["create-bill"],
        permissionKey: "BillingInvoices",
      },
    ],
  },
  {
    path: "/notifications",
    id: "Employee",
    element: <AllNotifications />,
    menuName: "Notifications",
    icon: Bell,
    highlightOn: ["notifications"],
  },
  {
    path: "/profile",
    id: "Employee",
    element: <EditProfile />,
    menuName: "Profile",
    icon: User,
    highlightOn: ["profile"],
  },
];

export default employeeRoutes;
